
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAutoPaginationNavigation } from '@/utils/autoPaginationNavigation';
import {
  useGetScheduleByIdQuery,
  useUpdateScheduleMutation,
  useGetActivityTypesDropdownQuery,
  useSearchAssetsQuery,
  useGetUserGroupsDropdownQuery,
  useGetCostTemplatesDropdownQuery,
  useGetChecklistTemplatesDropdownQuery,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation
} from '../../store/api/scheduleApi';
import {
  ScheduleUpdateInput,
  ScheduleAttachment,
  FrequencyData,
  AssetSearchResult,
} from '../../types/schedule.types';
import { DocumentMetadata } from '@/services/documentService';
import { ScheduleStatus, getScheduleStatusLabel } from '../../enums/schedule.enums';
import styles from '../AssetManagement/AssetCreate/AssetCreate.module.scss';
import { useGetSchedulableAssetsDropdownQuery } from '@/store/api/assetApi';

const ScheduleEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toListing, backToListing } = useAutoPaginationNavigation(navigate, '/schedule-management/schedules');
  
  // Form state
  const [formData, setFormData] = useState<ScheduleUpdateInput>({
    name: '',
    description: '',
    activity_type_id: '',
    asset_id: '',
    user_group_id: '',
    start_date: '',
    end_date: '',
    execution_before: 0,
    reminder_before: 0,
    cost_template_id: '',
    checklist_template_id: '',
    frequency_type: 'daily',
    frequency_interval: 1,
    execution_periods: [
      {
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }
    ],
    templates: [],
    status: ScheduleStatus.ACTIVE,
    attachments: [],
    document_ids: [],
    movement_tracking_required: false,
    tracking_type: null,
  });
  
  // Local state for documents
  const [existingDocuments, setExistingDocuments] = useState<DocumentMetadata[]>([]);
  const [newDocuments, setNewDocuments] = useState<File[]>([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);
  
  // Local state for frequency data
  const [frequencyData, setFrequencyData] = useState<FrequencyData>({
    type: 'daily',
    interval: 1,
    days_of_week: [],
    days_of_month: [],
    months: []
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [existingAttachments, setExistingAttachments] = useState<ScheduleAttachment[]>([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<AssetSearchResult | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; name: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // API hooks
  const { data: schedule, isLoading: isLoadingSchedule } = useGetScheduleByIdQuery(id || '');
  const { data: activityTypes, isLoading: isLoadingActivityTypes } = useGetActivityTypesDropdownQuery();
  const { data: assets, isLoading: isLoadingAssets } = useGetSchedulableAssetsDropdownQuery({isActive: true});
  const { data: assetSearchResults, isLoading: isSearchingAssets } = useSearchAssetsQuery(
    { query: assetSearchTerm, dropdown: true },
    { skip: assetSearchTerm.length < 2 }
  );
  const { data: userGroups, isLoading: isLoadingUserGroups } = useGetUserGroupsDropdownQuery();
  const { data: costTemplates, isLoading: isLoadingCostTemplates } =
    useGetCostTemplatesDropdownQuery();
  const { data: checklistTemplates, isLoading: isLoadingChecklistTemplates } = useGetChecklistTemplatesDropdownQuery();
  const [updateSchedule, { isLoading: isSubmitting }] = useUpdateScheduleMutation();
  const [uploadAttachment, { isLoading: isUploading }] = useUploadAttachmentMutation();
  const [deleteAttachment, { isLoading: isDeleting }] = useDeleteAttachmentMutation();
  
  // Initialize form data when schedule is loaded
  useEffect(() => {
    if (schedule) {
      // Set frequency data
      setFrequencyData({
        type: schedule.frequency_type || 'daily',
        interval: schedule.frequency || 1,
        days_of_week: schedule.frequency_days || [],
        days_of_month: [],
        months: schedule.frequency_months || []
      });
      
      const executionPeriods = [{
        start_date: schedule.start_date || new Date().toISOString().split('T')[0],
        end_date: schedule.end_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }];
      
      const templates = [];
      if (schedule.cost_template) {
        templates.push({
          template_id: schedule.cost_template.id,
          template_type: 'cost'
        });
      }
      
      if (schedule.checklist_template) {
        templates.push({
          template_id: schedule.checklist_template.id,
          template_type: 'checklist'
        });
      }
      
      // Set selected asset and location
      if (schedule.asset) {
        setSelectedAsset({
          id: schedule.asset.id,
          asset_tag: schedule.asset.asset_tag,
          asset_name: schedule.asset.asset_name,
          location: schedule.location || { id: '', name: '' }
        });
        
        // Set selected location from asset
        const locationTree = (schedule.asset as any).location_detail;
        if (locationTree) {
          console.log('Initial location set from location_tree:', locationTree);
          setSelectedLocation({
            id: locationTree.id,
            name: locationTree.name
          });
        } else if (schedule.location) {
          console.log('Initial location set from location:', schedule.location);
          setSelectedLocation(schedule.location);
        }
      }
      
      // Initialize existing documents from documents_detail
      if (schedule.documents_detail) {
        setExistingDocuments(schedule.documents_detail);
        // Initialize document_ids with existing document IDs
        const existingDocIds = schedule.documents_detail.map((doc: any) => doc.id);
        setFormData((prev: any) => ({
          ...prev,
          document_ids: existingDocIds
        }));
      }
      
      setFormData({
        name: schedule.name,
        description: schedule.description,
        activity_type_id: schedule.activity_type.id,
        asset_id: schedule.asset?.id || '',
        user_group_id: schedule.user_group?.id || '',
        start_date: schedule.start_date || '',
        end_date: schedule.end_date || '',
        execution_before: schedule.execution_before || 0,
        reminder_before: schedule.reminder_before || 0,
        cost_template_id: schedule.cost_template?.id || '',
        checklist_template_id: schedule.checklist_template?.id || '',
        frequency_type: schedule.frequency_type || 'daily',
        frequency_interval: schedule.frequency || 1,
        frequency_days: schedule.frequency_days || [],
        frequency_months: schedule.frequency_months || [],
        execution_periods: executionPeriods,
        templates: templates,
        status: schedule.status,
        attachments: [],
        document_ids: schedule.documents_detail ? schedule.documents_detail.map((doc: any) => doc.id) : [],
        movement_tracking_required: schedule.movement_tracking_required || false,
        tracking_type: schedule.tracking_type || null,
      });
      
      // Set existing attachments
      if (schedule.attachments) {
        setExistingAttachments(schedule.attachments);
      }
    }
  }, [schedule]);
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
      
      // Clear error when field is updated
      if (errors[name]) {
        setErrors((prev: any) => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle asset selection
  const handleAssetSelect = (asset: AssetSearchResult | null) => {
    setSelectedAsset(asset);
    if (asset) {
      setFormData((prev: any) => ({ ...prev, asset_id: asset.id }));

      // Access location information from location_detail field based on API response structure
      const locationTree = (asset as any).location_detail;
      if (locationTree) {
        setSelectedLocation({
          id: locationTree.id,
          name: locationTree.name
        });
      }

      // Clear error when asset is selected
      if (errors.asset_id) {
        setErrors((prev: any) => ({ ...prev, asset_id: '' }));
      }
    } else {
      setFormData((prev: any) => ({ ...prev, asset_id: '' }));
      setSelectedLocation(null);
    }
  };
  
  // Handle existing document removal
  const handleExistingDocumentRemove = (documentId: string) => {
    // Add to removed documents list
    setRemovedDocumentIds((prev: any) => [...prev, documentId]);
    
    // Remove from existing documents display
    setExistingDocuments((prev: any) => prev.filter((doc: any) => doc.id !== documentId));
    
    // Update document_ids in form data (remove the deleted ID)
    setFormData((prev: any) => ({
      ...prev,
      document_ids: (prev.document_ids || []).filter((id: any) => id !== documentId)
    }));
  };
  
  // Handle frequency type change
  const handleFrequencyTypeChange = (type: string) => {
    setFrequencyData((prev: any) => ({
      ...prev,
      type,
      days_of_week: type === 'weekly' ? ['monday'] : [],
      days_of_month: type === 'monthly' ? [1] : [],
      months: type === 'yearly' ? ['january'] : []
    }));
    
    setFormData((prev: any) => ({
      ...prev,
      frequency_type: type,
      frequency_days: type === 'weekly' ? ['monday'] : [],
      frequency_months: type === 'yearly' ? ['january'] : []
    }));
  };
  
  // Handle frequency interval change
  const handleFrequencyIntervalChange = (interval: number) => {
    setFrequencyData((prev: any) => ({
      ...prev,
      interval
    }));
    
    setFormData((prev: any) => ({
      ...prev,
      frequency_interval: interval
    }));
  };
  
  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setNewDocuments((prev: any) => [...prev, ...newFiles]);
      setFormData((prev: any) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...newFiles]
      }));
    }
  };
  
  // Handle new file removal (files not yet uploaded)
  const handleNewFileRemove = (index: number) => {
    setNewDocuments((prev: any) => prev.filter((_: any, i: any) => i !== index));
    setFormData((prev: any) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_: any, i: any) => i !== index)
    }));
  };
  
  // Trigger file input click
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Handle existing attachment deletion
  const handleExistingAttachmentDelete = async (attachmentId: string) => {
    if (!id) return;
    
    try {
      await deleteAttachment({ scheduleId: id, attachmentId }).unwrap();
      setExistingAttachments((prev: any) => prev.filter((a: any) => a.id !== attachmentId));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const today = new Date().toISOString().split('T')[0];
    
    if (!formData.name.trim()) {
      newErrors.name = 'Schedule name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.activity_type_id) {
      newErrors.activity_type_id = 'Activity type is required';
    }
    
    if (!formData.asset_id) {
      newErrors.asset_id = 'Asset is required';
    }
    
    // Date validations - Start Date and End Date are mandatory
    if (!formData.start_date || !formData.start_date.trim()) {
      newErrors.start_date = 'Start date is required';
    } else if (formData.start_date < today) {
      newErrors.start_date = 'Start date cannot be in the past';
    }
    
    if (!formData.end_date || !formData.end_date.trim()) {
      newErrors.end_date = 'End date is required';
    } else {
      if (formData.end_date < today) {
        newErrors.end_date = 'End date cannot be in the past';
      }
      
      if (formData.start_date && formData.end_date <= formData.start_date) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    // Movement tracking validation
    if (formData.movement_tracking_required && !formData.tracking_type) {
      newErrors.tracking_type = 'Tracking type is required when movement tracking is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Always use FormData for consistency
      const formDataPayload = new FormData();

      // Add basic fields with proper backend field names
      formDataPayload.append('name', formData.name);
      formDataPayload.append('description', formData.description);
      
      // Convert ID fields to match backend expectations
      if (formData.activity_type_id) {
        formDataPayload.append('activity_type', formData.activity_type_id);
      }
      if (formData.asset_id) {
        formDataPayload.append('asset', formData.asset_id);
      }
      if (formData.user_group_id) {
        formDataPayload.append('user_group', formData.user_group_id);
      }
      if (formData.cost_template_id) {
        formDataPayload.append('cost_template', formData.cost_template_id);
      }
      if (formData.checklist_template_id) {
        formDataPayload.append('checklist_template', formData.checklist_template_id);
      }

      // Add date fields
      if (formData.start_date) {
        formDataPayload.append('start_date', formData.start_date);
      }
      if (formData.end_date) {
        formDataPayload.append('end_date', formData.end_date);
      }

      // Add numeric fields
      formDataPayload.append('execution_before', (formData.execution_before || 0).toString());
      formDataPayload.append('reminder_before', (formData.reminder_before || 0).toString());
      formDataPayload.append('frequency', (formData.frequency_interval || 1).toString());

      // Add boolean fields
      formDataPayload.append('status', formData.status);
      formDataPayload.append(
        'movement_tracking_required',
        (formData.movement_tracking_required || false).toString()
      );

      // Add tracking type only if movement tracking is required and tracking type is set
      if (formData.movement_tracking_required && formData.tracking_type) {
        formDataPayload.append('tracking_type', formData.tracking_type);
      }

      // Add frequency fields if present
      if (formData.frequency_type) {
        formDataPayload.append('frequency_type', formData.frequency_type);
      }
      if (formData.frequency_days && formData.frequency_days.length > 0) {
        formData.frequency_days.forEach((day: string) => {
          formDataPayload.append('frequency_days', day);
        });
      }
      if (formData.frequency_months && formData.frequency_months.length > 0) {
        formData.frequency_months.forEach((month: string) => {
          formDataPayload.append('frequency_months', month);
        });
      }

      // Add document IDs (existing documents to keep)
      if (formData.document_ids && formData.document_ids.length > 0) {
        formData.document_ids.forEach((docId: string) => {
          formDataPayload.append('document_ids', docId);
        });
      }

      // Add new file attachments
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file: File) => {
          formDataPayload.append('documents', file);
        });
      }

      await updateSchedule({ id: id || '', scheduleData: formDataPayload }).unwrap();
      backToListing();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      
      // Handle API errors
      if (error && typeof error === 'object' && 'data' in error) {
        const apiErrors = error.data as Record<string, string[]>;
        const formattedErrors: Record<string, string> = {};
        
        Object.entries(apiErrors).forEach(([key, messages]) => {
          formattedErrors[key] = Array.isArray(messages) ? messages[0] : messages as string;
        });
        
        setErrors(formattedErrors);
      }
      setErrorMessage('Failed to update schedule');
      setShowError(true);
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    backToListing();
  };
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  if (isLoadingSchedule) {
    return (
      <div className={`appWrapper ${styles.addAssetContainer}`}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }
  
  if (!schedule) {
    return (
      <div className={`appWrapper ${styles.addAssetContainer}`}>
        <h3 className="pageTitle">Error</h3>
        <div className={`${styles.AssetStepsContainer} bordered`}>
          <h4 className={styles.pageTitle}>Schedule Not Found</h4>
          <p>Error loading schedule details. The schedule may not exist or you may not have permission to view it.</p>
          <Button onClick={() => backToListing()}>
            Back to Schedules
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`appWrapper ${styles.addAssetContainer}`}>
      <h3 className="pageTitle">Edit Schedule: {schedule.name}</h3>

      <div className={styles.singlePageForm}>
        <div className={styles.formContent}>
          
          {/* Basic Information Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Basic Information</h4>
            <div className={styles.formContainer}>
              
              {/* Schedule Name */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Schedule Name*
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  variant="outlined"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter schedule name"
                />
              </div>

              {/* Activity Type */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Activity Type*
                </Typography>
                <FormControl fullWidth error={!!errors.activity_type_id}>
                  <Select
                    name="activity_type_id"
                    value={formData.activity_type_id}
                    onChange={handleChange}
                    displayEmpty
                    placeholder="Activity Type"
                  >
                    <MenuItem value="">Select Activity Type</MenuItem>
                    {isLoadingActivityTypes ? (
                      <MenuItem disabled>Loading activity types...</MenuItem>
                    ) : (
                      activityTypes?.map((type: any) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.activity_type_id && (
                    <FormHelperText>{errors.activity_type_id}</FormHelperText>
                  )}
                </FormControl>
              </div>

              {/* Asset Selection */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Asset*
                </Typography>
                <Autocomplete
                  options={assetSearchTerm.length >= 2 ? (assetSearchResults?.results || []) : (assets?.results || [])}
                  getOptionLabel={(option: any) => `${option.asset_name} (${option.asset_tag})`}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  loading={isSearchingAssets || isLoadingAssets}
                  value={selectedAsset}
                  placeholder="Select Asset"
                  onChange={(_: any, newValue: any) => {
                    // Process the asset before passing it to handleAssetSelect
                    if (newValue) {
                      // Ensure location_tree is available
                      const processedAsset = {
                        ...newValue,
                        // If location_tree is not available, try to use location
                        location_tree: (newValue as any).location_tree || newValue.location
                      };
                      handleAssetSelect(processedAsset);
                    } else {
                      handleAssetSelect(null);
                    }
                  }}
                  onInputChange={(_: any, newInputValue: any) => setAssetSearchTerm(newInputValue)}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      error={!!errors.asset_id}
                      helperText={errors.asset_id}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(isSearchingAssets || isLoadingAssets) ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>

              {/* Location (Read-only) */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Location
                </Typography>
                <TextField
                  fullWidth
                  value={selectedLocation?.name || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                  helperText="Auto-populated based on selected asset"
                  placeholder="Select Location"
                />
              </div>

              {/* Description */}
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Description*
                </Typography>
                <TextField
                  fullWidth
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={4}
                  placeholder="Enter Description"
                />
              </div>

              {/* Status Selection */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Status
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    displayEmpty
                    placeholder="Select Status"
                  >
                    <MenuItem value={ScheduleStatus.INACTIVE}>
                      {getScheduleStatusLabel(ScheduleStatus.INACTIVE)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.ACTIVE}>
                      {getScheduleStatusLabel(ScheduleStatus.ACTIVE)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.COMPLETED}>
                      {getScheduleStatusLabel(ScheduleStatus.COMPLETED)}
                    </MenuItem>
                    <MenuItem value={ScheduleStatus.CANCELLED}>
                      {getScheduleStatusLabel(ScheduleStatus.CANCELLED)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {/* Execution Period Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Execution Period</h4>
            <div className={styles.formContainer}>
              
              {/* Start Date */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Start Date*
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.start_date ? new Date(formData.start_date) : null}
                    onChange={(date) => {
                      const value = date && !isNaN(date.getTime())
                        ? date.toISOString().split('T')[0]
                        : '';
                      setFormData((prev: any) => ({ ...prev, start_date: value }));
                      if (errors.start_date) {
                        setErrors((prev) => ({ ...prev, start_date: '' }));
                      }
                    }}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_date,
                        helperText: errors.start_date,
                        size: 'small',
                      },
                      actionBar: {
                          actions: ['clear'],
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              {/* End Date */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  End Date*
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.end_date ? new Date(formData.end_date) : null}
                    onChange={(date) => {
                      const value = date && !isNaN(date.getTime())
                        ? date.toISOString().split('T')[0]
                        : '';
                      setFormData((prev: any) => ({ ...prev, end_date: value }));
                      if (errors.end_date) {
                        setErrors((prev) => ({ ...prev, end_date: '' }));
                      }
                    }}
                    minDate={
                      formData.start_date
                        ? new Date(formData.start_date)
                        : new Date()
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.end_date,
                        helperText: errors.end_date,
                        size: 'small',
                      },
                      actionBar: {
                          actions: ['clear'],
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              {/* Frequency Type */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Frequency Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={frequencyData.type}
                    onChange={(e) => handleFrequencyTypeChange(e.target.value as string)}
                    displayEmpty
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Frequency Interval */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Repeat Every
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={frequencyData.interval}
                  onChange={(e) =>
                    handleFrequencyIntervalChange(parseInt(e.target.value, 10))
                  }
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                        {frequencyData.type === 'daily' && 'Days'}
                        {frequencyData.type === 'weekly' && 'Weeks'}
                        {frequencyData.type === 'monthly' && 'Months'}
                        {frequencyData.type === 'yearly' && 'Years'}
                      </Box>
                    ),
                  }}
                  inputProps={{ min: 1 }}
                />
              </div>

              {/* User Group */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  User Group
                </Typography>
                <FormControl fullWidth error={!!errors.user_group_id}>
                  <Select
                    name="user_group_id"
                    value={formData.user_group_id}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="">None</MenuItem>
                    {isLoadingUserGroups ? (
                      <MenuItem disabled>Loading user groups...</MenuItem>
                    ) : userGroups &&
                      Array.isArray(userGroups) ?
                      userGroups.map((group: any) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      )) :
                      userGroups?.results && Array.isArray(userGroups.results) ?
                      userGroups.results.map((group: any) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      )) :
                      <MenuItem disabled>No user groups available</MenuItem>
                    }
                  </Select>
                  {errors.user_group_id && (
                    <FormHelperText>{errors.user_group_id}</FormHelperText>
                  )}
                </FormControl>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Notifications</h4>
            <div className={styles.formContainer}>
              
              {/* Execution Before */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Execution Before (days)
                </Typography>
                <TextField
                  fullWidth
                  name="execution_before"
                  type="number"
                  value={formData.execution_before}
                  onChange={handleChange}
                  error={!!errors.execution_before}
                  helperText="Days before due date to start execution"
                  inputProps={{ min: 0 }}
                  placeholder="Execution Before"
                />
              </div>

              {/* Reminder Before */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Reminder Before (days)
                </Typography>
                <TextField
                  fullWidth
                  name="reminder_before"
                  type="number"
                  value={formData.reminder_before}
                  onChange={handleChange}
                  error={!!errors.reminder_before}
                  helperText="Days before due date to send reminder"
                  inputProps={{ min: 0 }}
                  placeholder="Reminder Before"
                />
              </div>
            </div>
          </div>

          {/* Movement Tracking Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Movement Tracking</h4>
            <div className={styles.formContainer}>
              {/* Movement Tracking Required */}
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.movement_tracking_required}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          movement_tracking_required: e.target.checked,
                          // Reset tracking type if unchecked
                          tracking_type: e.target.checked ? prev.tracking_type : null,
                        }));
                        if (errors.tracking_type) {
                          setErrors((prev) => ({ ...prev, tracking_type: '' }));
                        }
                      }}
                      name="movement_tracking_required"
                    />
                  }
                  label="Asset Movement Tracking Required"
                />
              </div>

              {/* Tracking Type */}
              {formData.movement_tracking_required && (
                <div className={`${styles.formFields} ${styles.fullWidth}`}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Tracking Type*
                  </Typography>
                  <FormControl fullWidth error={!!errors.tracking_type}>
                    <Select
                      value={formData.tracking_type || ''}
                      onChange={(e) => {
                        const trackingType = e.target.value as 'vehicle' | 'asset';
                        setFormData((prev: any) => ({
                          ...prev,
                          tracking_type: trackingType,
                        }));
                        if (errors.tracking_type) {
                          setErrors((prev) => ({ ...prev, tracking_type: '' }));
                        }
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">Select Tracking Type</MenuItem>
                      <MenuItem value="asset">Asset Tracking</MenuItem>
                      <MenuItem value="vehicle">Vehicle Tracking</MenuItem>
                    </Select>
                    {errors.tracking_type && (
                      <FormHelperText>{errors.tracking_type}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            </div>
          </div>

          {/* Templates Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Templates</h4>
            <div className={styles.formContainer}>
              
              {/* Cost Template */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Cost Template
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="cost_template_id"
                    value={formData.cost_template_id}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="">None</MenuItem>
                    {isLoadingCostTemplates ? (
                      <MenuItem disabled>Loading cost templates...</MenuItem>
                    ) : costTemplates &&
                      'results' in costTemplates &&
                      Array.isArray(costTemplates.results) ? (
                      costTemplates.results.map((template: any) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    ) : Array.isArray(costTemplates) ? (
                      costTemplates.map((template: any) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No cost templates available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>

              {/* Checklist Template */}
              <div className={styles.formFields}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Checklist Template
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="checklist_template_id"
                    value={formData.checklist_template_id}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="">None</MenuItem>
                    {isLoadingChecklistTemplates ? (
                      <MenuItem disabled>Loading checklist templates...</MenuItem>
                    ) : checklistTemplates &&
                      'results' in checklistTemplates &&
                      Array.isArray(checklistTemplates.results) ? (
                      checklistTemplates.results.map((template: any) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    ) : Array.isArray(checklistTemplates) ? (
                      checklistTemplates.map((template: any) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No checklist templates available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {/* Attachments Section */}
          <div className={`${styles.AssetStepsContainer} bordered`}>
            <h4 className={styles.pageTitle}>Attachments</h4>
            <div className={styles.formContainer}>
              
              {/* File Upload */}
              <div className={`${styles.formFields} ${styles.fullWidth}`}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Upload Files
                </Typography>
                <div className={styles.fileUploadContainer}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleFileButtonClick}
                    disabled={isUploading}
                    className={styles.uploadButton}
                  >
                    {isUploading ? 'Uploading...' : 'Choose Files'}
                  </Button>
                </div>
              </div>

              {/* Existing Documents */}
              {existingDocuments.length > 0 && (
                <div className={`${styles.formFields} ${styles.fullWidth}`}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Existing Documents
                  </Typography>
                  <List className={styles.fileList}>
                    {existingDocuments.map((doc: any) => (
                      <ListItem key={doc.id} className={styles.fileListItem}>
                        <ListItemText
                          primary={doc.original_name || doc.name}
                          secondary={`${formatFileSize(doc.size || 0)} • Uploaded: ${new Date(doc.created_at).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleExistingDocumentRemove(doc.id)}
                            disabled={isDeleting}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </div>
              )}

              {/* New Documents */}
              {newDocuments.length > 0 && (
                <div className={`${styles.formFields} ${styles.fullWidth}`}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    New Documents
                  </Typography>
                  <List className={styles.fileList}>
                    {newDocuments.map((file: File, index: number) => (
                      <ListItem key={index} className={styles.fileListItem}>
                        <ListItemText
                          primary={file.name}
                          secondary={`${formatFileSize(file.size)} • Ready to upload`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleNewFileRemove(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </div>
              )}

              {/* Existing Attachments */}
              {existingAttachments.length > 0 && (
                <div className={`${styles.formFields} ${styles.fullWidth}`}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Existing Attachments
                  </Typography>
                  <List className={styles.fileList}>
                    {existingAttachments.map((attachment: any) => (
                      <ListItem key={attachment.id} className={styles.fileListItem}>
                        <ListItemText
                          primary={attachment.original_filename}
                          secondary={`${formatFileSize(attachment.file_size)} • Uploaded: ${new Date(attachment.created_at).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleExistingAttachmentDelete(attachment.id)}
                            disabled={isDeleting}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className={styles.stickyFooter} style={{ marginTop: '10px' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="button button__solid--primary"
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              'Update Schedule'
            )}
          </Button>
        </div>
      </div>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ScheduleEditPage;